import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { http, money, errMsg, STATUS } from "@/lib/api";
import { buildPix } from "@/lib/pix";
import { useStore } from "@/context/StoreContext";

const copy = (t, msg) => { navigator.clipboard.writeText(t); toast.success(msg); };

const PixBox = ({ s, o }) => {
  const code = buildPix({ key: s.pix_key, name: s.pix_name, city: s.pix_city, amount: o.total, txid: o.code });
  return (
    <div className="rounded-3xl border-2 border-emerald-500/40 bg-emerald-500/5 p-6 sm:p-8" data-testid="pix-payment-box">
      <p className="font-heading font-bold text-xl text-emerald-300">Pague com Pix</p>
      <div className="grid sm:grid-cols-[auto,1fr] gap-6 mt-5 items-center">
        <div className="bg-white p-3 rounded-2xl w-fit" data-testid="pix-qr-code"><QRCodeSVG value={code} size={180} /></div>
        <div className="space-y-4 min-w-0">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Pix copia e cola</p>
            <p className="font-mono text-xs break-all text-slate-300 mt-1 line-clamp-3" data-testid="pix-copy-paste-code">{code}</p>
            <button onClick={() => copy(code, "Código Pix copiado!")} data-testid="pix-copy-code-button" className="mt-2 h-9 px-4 rounded-full bg-emerald-500 text-slate-950 font-semibold text-sm inline-flex items-center gap-2 hover:bg-emerald-400"><Copy className="w-4 h-4" /> Copiar código</button>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Chave Pix</p>
            <button onClick={() => copy(s.pix_key, "Chave Pix copiada!")} data-testid="pix-copy-key-button" className="font-mono text-sm text-slate-200 hover:text-emerald-300 inline-flex items-center gap-2">{s.pix_key} <Copy className="w-3.5 h-3.5" /></button>
            <p className="text-xs text-slate-500 mt-1">{s.pix_name}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MbwayBox = ({ s, o }) => (
  <div className="rounded-3xl border-2 border-rose-500/40 bg-rose-500/5 p-6 sm:p-8" data-testid="mbway-payment-box">
    <p className="font-heading font-bold text-xl text-rose-300">Pague com MB Way</p>
    <ol className="mt-4 space-y-2 text-sm text-slate-300 list-decimal list-inside">
      <li>Abra a app MB Way e escolha <b>Enviar dinheiro</b></li>
      <li>Envie o valor exato para o número abaixo</li>
      <li>Na mensagem, escreva a referência do pedido</li>
    </ol>
    <div className="grid sm:grid-cols-3 gap-4 mt-6">
      {[["Número MB Way", s.mbway_phone, "mbway-phone-number"], ["Valor", money(o.total, "EUR"), "mbway-amount"], ["Referência", o.code, "mbway-reference"]].map(([l, v, t]) => (
        <button key={t} onClick={() => copy(v, `${l} copiado!`)} className="text-left p-4 rounded-xl bg-white/5 border border-white/10 hover:border-rose-500/50">
          <p className="text-xs uppercase tracking-widest text-slate-500">{l}</p>
          <p className="font-mono font-bold mt-1" data-testid={t}>{v}</p>
        </button>
      ))}
    </div>
    {s.mbway_name && <p className="text-xs text-slate-500 mt-3">Titular: {s.mbway_name}</p>}
  </div>
);

export default function OrderPage() {
  const { id } = useParams();
  const { settings } = useStore();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const inp = useRef();

  const load = () => http.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => setO(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await http.post(`/orders/${id}/proof`, fd); toast.success("Comprovativo enviado!"); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  if (o === false) return <p className="max-w-3xl mx-auto px-8 py-24 text-slate-400" data-testid="order-not-found">Pedido não encontrado.</p>;
  if (!o || !settings) return <p className="max-w-3xl mx-auto px-8 py-24 text-slate-500">Carregando pedido...</p>;
  const st = STATUS[o.status];
  const canUpload = ["pendente", "em_analise"].includes(o.status);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 space-y-8" data-testid="order-page">
      <div className="rise">
        <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Pedido</p>
        <div className="flex flex-wrap items-center gap-4 mt-2">
          <h1 className="font-heading font-extrabold text-4xl tracking-tight font-mono" data-testid="order-code">{o.code}</h1>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${st.cls}`} data-testid="order-status">{st.label}</span>
        </div>
        <p className="text-slate-400 mt-3">Guarde este link para acompanhar o seu pedido. Total: <span className="font-mono text-white font-bold" data-testid="order-total">{money(o.total, o.currency)}</span></p>
      </div>
      {o.status === "entregue" && (
        <div className="rounded-3xl border-2 border-violet-500/40 bg-violet-500/10 p-6 flex items-center gap-4" data-testid="order-delivered-box">
          <CheckCircle2 className="w-8 h-8 text-violet-300 shrink-0" />
          <div><p className="font-heading font-bold text-lg">Itens entregues!</p><p className="text-sm text-slate-300">Os itens foram enviados para o nick <span className="font-mono text-white">{o.nickname}</span> no servidor.</p></div>
        </div>
      )}
      {o.status === "pago" && (
        <p className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm text-emerald-200" data-testid="order-paid-box">Pagamento confirmado! Os itens vão ser enviados para <span className="font-mono">{o.nickname}</span> em breve. Entre no servidor para recebê-los.</p>
      )}
      {canUpload && (o.payment_method === "pix" ? <PixBox s={settings} o={o} /> : <MbwayBox s={settings} o={o} />)}
      {canUpload && (
        <div className="rounded-3xl border border-dashed border-white/20 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between" data-testid="proof-upload-box">
          <div>
            <p className="font-heading font-bold text-lg">{o.has_proof ? "Comprovativo recebido" : "Já pagou? Envie o comprovativo"}</p>
            <p className="text-sm text-slate-400 mt-1">{o.has_proof ? "Estamos a verificar. Pode enviar outro se necessário." : "Imagem (JPG, PNG) ou PDF. Assim confirmamos mais rápido."}</p>
          </div>
          <input ref={inp} type="file" accept="image/*,.pdf" className="hidden" onChange={upload} data-testid="upload-payment-proof-input" />
          <button onClick={() => inp.current.click()} disabled={busy} data-testid="upload-payment-proof-button"
            className="h-12 px-6 rounded-full bg-white text-slate-950 font-semibold inline-flex items-center gap-2 hover:bg-slate-200 disabled:opacity-50 shrink-0">
            {o.has_proof ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Upload className="w-4 h-4" />} {busy ? "Enviando..." : "Enviar comprovativo"}
          </button>
        </div>
      )}
      <div className="rounded-3xl bg-[#111827] border border-white/10 p-6" data-testid="order-items">
        <p className="font-heading font-bold">Itens</p>
        {o.items.map((i) => (
          <div key={i.product_id} className="flex justify-between text-sm py-3 border-b border-white/5 last:border-0">
            <span>{i.quantity}× {i.name}</span><span className="font-mono">{money(i.unit_price * i.quantity, o.currency)}</span>
          </div>
        ))}
        <p className="text-sm text-slate-400 mt-4">Entrega para o nick: <span className="font-mono text-slate-200">{o.nickname}</span></p>
      </div>
    </div>
  );
}
